import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getUser, clearAuth, getPropertyConfig, getProperty } from '../utils/auth';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  FileText,
  BarChart3,
  Users,
  LogOut,
  Menu,
  History,
  ClipboardCheck,
  QrCode
} from 'lucide-react';
import { Button } from './ui/button';
import { useState } from 'react';

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();
  const propertyConfig = getPropertyConfig();
  const propertyId = getProperty();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const hasPO = propertyConfig?.has_po !== false;

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['Admin', 'Purchasing', 'Finance', 'Receiving', 'StoreRequisition'] },
    { name: 'Master Data', href: '/master-data', icon: FileText, roles: ['Admin', 'Purchasing'] },
    ...(hasPO ? [{ name: 'Purchase Orders', href: '/purchase-orders', icon: ShoppingCart, roles: ['Admin', 'Purchasing', 'Finance'] }] : []),
    { name: hasPO ? 'Receiving' : 'Barang Masuk', href: '/receiving', icon: Package, roles: ['Admin', 'Receiving'] },
    { name: hasPO ? 'Store Requisition' : 'Barang Keluar', href: '/store-requisitions', icon: FileText, roles: ['Admin', 'StoreRequisition'] },
    { name: 'Stock Opname', href: '/stock-opname', icon: ClipboardCheck, roles: ['Admin'] },
    { name: 'Stock Reports', href: '/reports', icon: BarChart3, roles: ['Admin', 'Purchasing', 'Finance', 'Receiving', 'StoreRequisition'] },
    ...(hasPO ? [{ name: 'PO by User', href: '/po-by-user', icon: Users, roles: ['Admin', 'Purchasing', 'Finance'] }] : []),
    { name: 'QR Scanner', href: '/qr-scanner', icon: QrCode, roles: ['Admin', 'Purchasing', 'Finance', 'Receiving', 'StoreRequisition'] },
    { name: 'Activity Logs', href: '/activity-logs', icon: History, roles: ['Admin'] },
    { name: 'Users', href: '/users', icon: Users, roles: ['Admin'] },
  ];

  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const filteredNav = navigation.filter(item => item.roles.some(r => userRoles.includes(r)));

  const propertyTitle = propertyConfig?.short_name || 'RTS';
  const propertySubtitle = propertyConfig?.name || 'Royal Tulip Batu';

  return (
    <div className="min-h-screen flex noise-texture">
      {/* Sidebar */}
      <aside className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static brand-gradient text-white shadow-xl`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-white/20">
            <div>
              <h1 className="text-2xl font-bold" data-testid="app-title">{propertyTitle} Inventory</h1>
              <p className="text-xs opacity-80 mt-1">{propertySubtitle}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-white hover:bg-white/10"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-button"
            >
              <Menu className="h-6 w-6" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {filteredNav.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-white/20 shadow-md'
                      : 'hover:bg-white/10'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-white/20">
            <div className="mb-4 p-3 bg-white/10 rounded-lg">
              <p className="text-sm font-medium">{user?.full_name || user?.username}</p>
              <p className="text-xs opacity-80">{(user?.roles || [user?.role]).filter(Boolean).join(', ')}</p>
              {user?.departments && user.departments.length > 0 && (
                <p className="text-xs opacity-60 mt-1">Dept: {user.departments.join(', ')}</p>
              )}
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="glassmorphism-header sticky top-0 z-40">
          <div className="flex items-center justify-between px-6 py-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="open-sidebar-button"
            >
              <Menu className="h-6 w-6" />
            </Button>
            <div className="flex-1" />
            <div className="text-sm text-muted-foreground">
              {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-secondary">
          <div className="container mx-auto p-6 lg:p-8 max-w-7xl">
            {children}
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t bg-white shadow-inner">
          <div className="container mx-auto px-6 py-4">
            <p className="text-sm text-center font-medium" style={{color: '#1A4D2E'}}>
              Copyright &copy; 2025 created by <span className="font-bold" style={{color: '#C84C32'}}>luckytech</span>
            </p>
          </div>
        </footer>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}
    </div>
  );
}
